import { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { 
  Elements, 
  PaymentElement,
  useStripe, 
  useElements
} from '@stripe/react-stripe-js';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

interface Props {
  clientSecret: string;
  orderNumber: string; // Required for payment verification
  onSuccess: () => void;
  onError: (error: string) => void;
  onBack: () => void;
}

function CheckoutForm({ orderNumber, onError, onBack }: Props) {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      console.error('Stripe sau Elements nu sunt inițializate');
      return;
    }

    setIsLoading(true);

    try {
      console.log('Se începe confirmarea plății...');
      const returnUrl = `${window.location.origin}/payment-success/${orderNumber}`;
      console.log('URL de redirecționare:', returnUrl, 'Order number:', orderNumber);
      
      const { error: submitError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl,
        }
      });

      if (submitError) {
        console.error('Eroare la plată:', submitError);
        const errorMessage = submitError.message || 'A apărut o eroare la procesarea plății';
        setError(errorMessage);
        onError(errorMessage);
      }
    } catch (error) {
      console.error('Eroare la plată:', error);
      const errorMessage = 'A apărut o eroare la procesarea plății';
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="mb-6 text-center">
        <h3 className="text-base font-bold text-white">Plată securizată cu cardul</h3>
        <p className="mt-1 text-sm text-gray-400">Toate tranzacțiile sunt procesate în siguranță prin Stripe</p>
      </div>

      <div className="bg-gray-900/50 p-4 rounded-lg border border-red-500/20">
        <PaymentElement 
          options={{
            layout: {
              type: 'accordion',
              defaultCollapsed: false,
              radios: true,
              spacedAccordionItems: true
            },
            defaultValues: {
              billingDetails: {
                name: '',
                email: '',
                phone: '',
                address: {
                  country: 'RO',
                }
              }
            }
          }}
          onChange={() => {
            // Handle change if needed
          }}
        />
      </div>

      {error && (
        <div className="p-3 bg-red-900/20 border border-red-500 rounded-lg">
          <p className="text-red-200 text-sm text-center">{error}</p>
        </div>
      )}

      <div className="flex justify-between gap-3 pt-2">
        <button
          type="button"
          onClick={onBack}
          disabled={isLoading}
          className="flex-1 bg-gray-800 text-white px-4 py-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Înapoi
        </button>

        <button
          type="submit"
          disabled={!stripe || isLoading}
          className="flex-1 bg-gradient-to-r from-red-600 to-red-700 text-white px-4 py-2 rounded-md hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Se procesează...' : 'Plătește acum'}
        </button>
      </div>
    </form>
  );
}

export function StripeCheckout({ clientSecret, orderNumber, onSuccess, onError, onBack }: Props) {
  if (!stripePromise) {
    console.error('Stripe nu s-a putut încărca');
    return (
      <div className="p-4 bg-red-900/20 border border-red-500 rounded-lg">
        <p className="text-red-200 text-center">Nu s-a putut încărca formularul de plată</p>
      </div>
    );
  }
  
  if (!clientSecret) {
    console.error('Client secret lipsește');
    return (
      <div className="p-4 bg-red-900/20 border border-red-500 rounded-lg">
        <p className="text-red-200 text-center">Nu s-a putut inițializa plata</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <Elements 
        stripe={stripePromise} 
        options={{
          clientSecret,
          appearance: {
            theme: 'night',
            variables: {
              colorPrimary: '#EF4444',
              colorBackground: '#111827',
              colorText: '#FFFFFF',
              colorDanger: '#DC2626',
              fontFamily: 'system-ui, sans-serif',
              borderRadius: '6px',
              spacingUnit: '4px'
            },
            rules: {
              '.Input': {
                border: '1px solid #374151',
                boxShadow: 'none',
                backgroundColor: '#1F2937'
              },
              '.Input:focus': {
                border: '1px solid #EF4444',
                boxShadow: '0 0 0 1px #EF4444'
              },
              '.Label': {
                color: '#9CA3AF'
              },
              '.Tab': {
                border: '1px solid #374151',
                backgroundColor: '#1F2937'
              },
              '.Tab:hover': {
                color: '#FFFFFF',
                border: '1px solid #EF4444'
              },
              '.Tab--selected': {
                border: '1px solid #EF4444',
                backgroundColor: '#991B1B'
              }
            }
          },
          locale: 'ro'
        }}
      >
        <CheckoutForm 
          clientSecret={clientSecret}
          orderNumber={orderNumber}
          onSuccess={onSuccess}
          onError={onError}
          onBack={onBack}
        />
      </Elements>
    </div>
  );
}
